class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
    this.updateUrl = this.dataset.updateUrl === 'true';
    this.setupPlanPickerListener();
  }

  connectedCallback() {
    // Wait for DOM to be ready and subscription widget to render
    setTimeout(() => {
      this.setupInitialState();
    }, 100);
  }

  setupInitialState() {
    // Initial setup of variant visibility
    this.updateSizeOptionsVisibility();
    
    // Check initial variant availability and update button state
    this.updateOptions();
    this.updateVariantId();
    
    if (this.currentVariant && !this.currentVariant.available) {
      this.toggleAddButton(true, 'Out of Stock', true);
    }
  }

  setupPlanPickerListener() {
    // Listen for plan picker changes (subscription vs one-time)
    document.body.addEventListener('change', (event) => {
      if (event.target.matches('input[name="nw-plan-picker"]')) {
        // Re-check button state when plan type changes
        if (this.currentVariant && !this.currentVariant.available) {
          this.toggleAddButton(true, 'Out of Stock', true);
        } else {
          this.toggleAddButton(false, '', false);
        }
      }
    });
  }

  onVariantChange() {
    //console.log('Variant change triggered');
    this.updateOptions();
    this.updateSizeOptionsVisibility();
    this.updateVariantId();
    
    if (!this.currentVariant) {
      //console.log('No variant found');
      this.toggleAddButton(true, 'Unavailable', true);
      this.setUnavailable();
    } else {
      //console.log('Current variant:', this.currentVariant);
      this.updateURL();
      this.updateVariantInput();
      this.updateSelectedVariantId();
      this.refreshSubscriptionWidget();
      
      // Check if variant is unavailable
      if (!this.currentVariant.available) {
        this.toggleAddButton(true, 'Out of Stock', true);
      } else {
        this.toggleAddButton(false, '', false);
      }
    }
  }

  updateSelectedVariantId() {
    if (!this.currentVariant) return;
    // Update the data-selected-id attribute on the variant-selects element
    this.setAttribute('data-selected-id', this.currentVariant.id);
  }

  updateSizeOptionsVisibility() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset')).filter((fieldset) => fieldset.querySelector('legend'));
    if (!fieldsets.length) return;

    const selectedValues = fieldsets.map((fieldset) => {
      const checkedInput = fieldset.querySelector('input:checked');
      return checkedInput ? checkedInput.value : null;
    });

    const variants = this.getVariantData();

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      const inputs = Array.from(fieldset.querySelectorAll('input'));
      if (!inputs.length) return;

      const allowedValues = new Set();
      const allValues = new Set();

      variants.forEach((variant) => {
        const variantOptions = this.getVariantOptionsArray(variant);
        if (!variantOptions.length) return;

        let matchesOtherSelections = true;
        for (let otherIndex = 0; otherIndex < fieldsetIndex; otherIndex += 1) {
          const selectedValue = selectedValues[otherIndex];
          if (!selectedValue) continue;
          if (variantOptions[otherIndex] !== selectedValue) {
            matchesOtherSelections = false;
            break;
          }
        }

        if (matchesOtherSelections) {
          allValues.add(variantOptions[fieldsetIndex]);
          if (variant.available) {
            allowedValues.add(variantOptions[fieldsetIndex]);
          }
        }
      });

      if (!allValues.size) return;

      let selectionChanged = false;

      inputs.forEach((input) => {
        const label = fieldset.querySelector(`label[for=${CSS.escape(input.id)}]`);
        
        // Show all options that match other selections
        if (allValues.has(input.value)) {
          input.style.removeProperty('display');
          if (label) {
            label.style.removeProperty('display');
            
            // Add or remove unavailable class based on availability
            if (allowedValues.has(input.value)) {
              label.classList.remove('variant-unavailable');
              input.removeAttribute('data-unavailable');
            } else {
              label.classList.add('variant-unavailable');
              input.setAttribute('data-unavailable', 'true');
            }
          }
        } else {
          // Hide options that don't match other selections
          input.style.display = 'none';
          if (label) label.style.display = 'none';
          if (input.checked) {
            input.checked = false;
            selectionChanged = true;
          }
        }
      });

      if (selectionChanged) {
        const fallbackOption = inputs.find((input) => allowedValues.has(input.value) && allValues.has(input.value));
        if (fallbackOption) {
          fallbackOption.checked = true;
          selectedValues[fieldsetIndex] = fallbackOption.value;
          fallbackOption.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
    // console.log('Updated options:', this.options);
  }

  updateVariantId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options.some((option, index) => {
        return this.options[index] !== option;
      });
    });
  }

  updateURL() {
    if (!this.currentVariant || !this.updateUrl) return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  refreshSubscriptionWidget() {
    //console.log('Attempting to refresh subscription widget');
    const subscriptionWidget = document.getElementById('nw-subscription-widget');
    
    if (!subscriptionWidget) {
      console.log('No subscription widget found in DOM');
      return;
    }
    
    // Store the current selection before refresh
    const currentPlanSelection = document.querySelector('input[name="nw-plan-picker"]:checked')?.value || 'one-time';
    
    //console.log('Found subscription widget:', subscriptionWidget);
    //console.log('Current variant ID:', this.currentVariant.id);
    //console.log('Section ID:', this.dataset.section);

    // First try to get the section containing the subscription widget
    const sectionContainer = subscriptionWidget.closest('.shopify-section');
    const sectionId = sectionContainer ? sectionContainer.id.replace('shopify-section-', '') : this.dataset.section;

    const url = `${window.location.pathname}?variant=${this.currentVariant.id}&section_id=${sectionId}`;
    //console.log('Fetching from URL:', url);

    fetch(url)
      .then(response => response.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newSubscriptionWidget = doc.getElementById('nw-subscription-widget');
        
        if (newSubscriptionWidget) {
          subscriptionWidget.innerHTML = newSubscriptionWidget.innerHTML;
          
          // Restore the previous selection
          const planInput = subscriptionWidget.querySelector(`input[name="nw-plan-picker"][value="${currentPlanSelection}"]`);
          if (planInput) {
            planInput.checked = true;
            // Trigger change event to ensure any dependent UI updates occur
            planInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      })
      .catch(error => {
        console.error('Error refreshing subscription widget:', error);
      });
  }

  reinitializeWidgetScripts(widget) {
    // Re-run any scripts that need to be initialized
    const scripts = widget.querySelectorAll('script');
    scripts.forEach(oldScript => {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(attr => {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  toggleAddButton(disable = true, text = '', modifyClass = true) {
    // Get the currently checked plan type
    const checkedPlanInput = document.querySelector('input[name="nw-plan-picker"]:checked');
    if (!checkedPlanInput) return;
    
    const checkedPlanValue = checkedPlanInput.value;
    
    // Find the CTA button in the currently active plan section
    let addButton;
    if (checkedPlanValue === 'subscription') {
      addButton = document.querySelector('.nw-plan-picker-item--subscription .nw-plan-picker-cta-button');
    } else if (checkedPlanValue === 'one-time') {
      addButton = document.querySelector('.nw-plan-picker-item--one-time .nw-plan-picker-cta-button');
    }
    
    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) {
        // Store the original button text if not already stored
        if (!addButton.dataset.originalText) {
          const buttonTextSpan = addButton.querySelector('span');
          if (buttonTextSpan) {
            addButton.dataset.originalText = buttonTextSpan.textContent;
          }
        }
        const buttonTextSpan = addButton.querySelector('span');
        if (buttonTextSpan) {
          buttonTextSpan.textContent = text;
        }
      }
    } else {
      addButton.removeAttribute('disabled');
      // Restore original button text or use default
      const originalText = addButton.dataset.originalText || 'Add to cart';
      const buttonTextSpan = addButton.querySelector('span');
      if (buttonTextSpan) {
        buttonTextSpan.textContent = originalText;
      }
    }

    if (!modifyClass) return;
    
    if (disable) {
      addButton.classList.add('disabled');
    } else {
      addButton.classList.remove('disabled');
    }
  }

  setUnavailable() {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;

    const addButton = productForm.querySelector('[name="add"]');
    if (!addButton) return;

    addButton.textContent = 'Unavailable';
    if (this.currentVariant) {
      document.getElementById(`price-${this.dataset.section}`)?.classList.add('hidden');
    }
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }

  getVariantOptionsArray(variant) {
    if (Array.isArray(variant.options) && variant.options.length) {
      return variant.options;
    }

    const fallbackOptions = [];
    if (variant.option1) fallbackOptions.push(variant.option1);
    if (variant.option2) fallbackOptions.push(variant.option2);
    if (variant.option3) fallbackOptions.push(variant.option3);
    return fallbackOptions;
  }
}

customElements.define('variant-selects', VariantSelects);
